/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, InputAdornment, MenuItem, useTheme, Skeleton, TextField } from '@mui/material';
import ManageRetailersTable from './ManageRetailersTable';
import { SearchLg, Trash01 } from '../../../../assets';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { RetailerHeaderModel } from '../../../../app/services/api/ofdClient';
import { OnboardingStatusEnum } from '../../Onboarding/enums/OnboardingStatusEnum';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import useLogError from '../../../../hooks/useLogError';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import { UserContext } from '../../../../components/shared/useUser';

const inputStyling = {
  width: '320px',
  '& .MuiInputBase-root': {
    height: '40px',
  },
};

const onboardingStatusOptions = [
  { value: 0, label: 'All' },
  { value: OnboardingStatusEnum.DRAFT, label: 'Draft' },
  { value: OnboardingStatusEnum.ACTIVE, label: 'Active' },
];

const ManageRetailersBox = (props: {
  retailers: RetailerHeaderModel[], transformedData: RetailerHeaderModel[],
  loadData: () => void, isLoading: boolean, onCreate: () => void, setTransformedData: Function,
  page: number, setPage: (page: number) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const { deleteRetailerById } = useNswagClient();
  const { t } = useTranslation('manageRetailers');
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRetailer, setSelectedRetailer] = useState<RetailerHeaderModel | undefined>(undefined);
  const [showDeleteSuccessMessage, setshowDeleteSuccessMessage] = useState(false);
  const { logError } = useLogError();
  const { hasPermissionTo } = useContext(UserContext);

  useEffect(() => {
    setSearchTerm('');
    setStatusFilter('0');
    props.setTransformedData(props.retailers ?? []);
  }, [props.retailers]);

  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      let filteredData = [...props.retailers];
      if (searchTerm && searchTerm !== '') {
        filteredData = filteredData.filter(x =>
          x.name?.toLowerCase().includes(searchTerm.toLowerCase()));
      }
      if (statusFilter !== '' && statusFilter !== '0') {
        filteredData = filteredData.filter(x => x.statusId === Number(statusFilter));
      }
      props.setPage(0);
      props.setTransformedData(filteredData);
    }, 400);
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, [searchTerm, statusFilter, props.retailers]);

  const handleClear = () => {
    setSearchTerm('');
    setStatusFilter('0');
    props.setPage(0);
  };

  const handleSearch = (retailerName: string) => {
    setSearchTerm(retailerName);
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
  };

  const deleteRetailer = (retailer: RetailerHeaderModel) => {
    setSelectedRetailer(retailer);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    deleteRetailerById(selectedRetailer?.retailerId ?? 0)
      .then(() => {
        props.loadData();
        setshowDeleteSuccessMessage(true);
      })
      .catch((error: any) => logError(error));

    setShowDeleteModal(false);
    setSelectedRetailer(undefined);
  };

  return (
    <>
      <Grid item
        mt={5}
        xs={12}>
        <Box paddingY={5}
          sx={{ display: 'flex', gap: 4, width: '100%' }}>
          <TextField
            onChange={(event) => handleSearch(event.target.value)}
            size='small'
            placeholder={t('search')}
            value={searchTerm}
            sx={{ ...inputStyling }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchLg />
                </InputAdornment>
              ),
            }} />
          <TextField
            select
            size='small'
            label={t('status')}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ ...inputStyling }}
          >
            {onboardingStatusOptions.map(opt => (
              <MenuItem key={opt.value}
                value={String(opt.value)}>
                {t(opt.label) || opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            disabled={searchTerm === '' && statusFilter === ''}
            variant="secondary"
            size="lg"
            onClick={handleClear}
            sx={{ width: '160px' }}
          >
            {t('clearFilters')}
          </Button>
        </Box>

        {props.isLoading
          ? <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
            variant='rectangular'
            height={445.89} />
          : props.transformedData.length === 0
            ?
            <EmptyTable
              text={t('retailers')}
              searchTerm={searchTerm}
              onClear={handleClear}
              onAdd={() => props.onCreate()}
              addItemText={t('newRetailer')}
              onAddIsCreating={!hasPermissionTo(['RetailersCreate'])}
              withPaginationShown={false}
              headerHeight={55}
              bodyRowHeight={45} />
            :
            <ManageRetailersTable
              page={props.page}
              setPage={props.setPage}
              retailers={props.transformedData}
              deleteRetailer={deleteRetailer} />}

        <MessagesSnackbar open={showDeleteSuccessMessage}
          onClose={() => { setshowDeleteSuccessMessage(false); }}
          message={t('retailerDeletedMessage')}
          severity='success'
          duration={5000} />
      </Grid>
      <ConfirmationDialog open={showDeleteModal}
        onCancel={() => handleCloseModal()}
        onConfirm={() => handleDelete()}
        icon={<Trash01 />}
        title={t('deleteModal.confirm')}
        message={t('deleteModal.confirmMessage')}
        confirmButtonText={t('deleteModal.yes')}
        cancelButtonText={t('deleteModal.no')} /></>
  );
};

export default ManageRetailersBox;